import { toHiragana } from 'wanakana';

const normalizationOptions = {
    convertLongVowelMark: false,
    customKanaMapping: {
        tcha: 'っちゃ',
        tchi: 'っち',
        tchu: 'っちゅ',
        tche: 'っちぇ',
        tcho: 'っちょ',
    },
} as const;

const fullSizeEndingKana: Record<string, string> = {
    'ぁ': 'あ',
    'ぃ': 'い',
    'ぅ': 'う',
    'ぇ': 'え',
    'ぉ': 'お',
    'っ': 'つ',
    'ゃ': 'や',
    'ゅ': 'ゆ',
    'ょ': 'よ',
    'ゎ': 'わ',
    'ゕ': 'か',
    'ゖ': 'け',
};

export function normalizeToHiragana(input: string): string {
    // Traditional Hepburn uses "m" before b/p, as in shimbun and tempura.
    const normalizedHepburn = input.replace(/m(?=[bp])/gi, 'n');
    return toHiragana(normalizedHepburn, normalizationOptions);
}

export function getShiritoriEnding(input: string): string | undefined {
    const withoutTrailingLongVowels = input.replace(/ー+$/u, '');
    const ending = normalizeToHiragana(withoutTrailingLongVowels).at(-1);
    return ending ? fullSizeEndingKana[ending] ?? ending : undefined;
}

/**
 * @deprecated Keeping this here just for memory sake
 */
function romajiToHiragana(input: string): string {
    const romajiMap: Record<string, string> = {
        kya: 'きゃ', kyu: 'きゅ', kyo: 'きょ',
        sha: 'しゃ', shu: 'しゅ', sho: 'しょ',
        cha: 'ちゃ', chu: 'ちゅ', cho: 'ちょ',
        nya: 'にゃ', nyu: 'にゅ', nyo: 'にょ',
        hya: 'ひゃ', hyu: 'ひゅ', hyo: 'ひょ',
        mya: 'みゃ', myu: 'みゅ', myo: 'みょ',
        rya: 'りゃ', ryu: 'りゅ', ryo: 'りょ',
        gya: 'ぎゃ', gyu: 'ぎゅ', gyo: 'ぎょ',
        ja: 'じゃ', ju: 'じゅ', jo: 'じょ',
        bya: 'びゃ', byu: 'びゅ', byo: 'びょ',
        pya: 'ぴゃ', pyu: 'ぴゅ', pyo: 'ぴょ',
        shi: 'し', chi: 'ち', tsu: 'つ', fu: 'ふ', ji: 'じ',
        ka: 'か', ki: 'き', ku: 'く', ke: 'け', ko: 'こ',
        sa: 'さ', si: 'し', su: 'す', se: 'せ', so: 'そ',
        ta: 'た', ti: 'ち', tu: 'つ', te: 'て', to: 'と',
        na: 'な', ni: 'に', nu: 'ぬ', ne: 'ね', no: 'の',
        ha: 'は', hi: 'ひ', hu: 'ふ', he: 'へ', ho: 'ほ',
        ma: 'ま', mi: 'み', mu: 'む', me: 'め', mo: 'も',
        ya: 'や', yu: 'ゆ', yo: 'よ',
        ra: 'ら', ri: 'り', ru: 'る', re: 'れ', ro: 'ろ',
        wa: 'わ', wo: 'を',
        ga: 'が', gi: 'ぎ', gu: 'ぐ', ge: 'げ', go: 'ご',
        za: 'ざ', zi: 'じ', zu: 'ず', ze: 'ぜ', zo: 'ぞ',
        da: 'だ', di: 'ぢ', du: 'づ', de: 'で', 'do': 'ど',
        ba: 'ば', bi: 'び', bu: 'ぶ', be: 'べ', bo: 'ぼ',
        pa: 'ぱ', pi: 'ぴ', pu: 'ぷ', pe: 'ぺ', po: 'ぽ',
        a: 'あ', i: 'い', u: 'う', e: 'え', o: 'お',
        n: 'ん',
    };

    let result = '';
    let index = 0;

    while (index < input.length) {
        const three = input.slice(index, index + 3);
        const two = input.slice(index, index + 2);
        const one = input[index]!;

        if (romajiMap[three]) {
            result += romajiMap[three]!;
            index += 3;
            continue;
        }

        if (romajiMap[two]) {
            result += romajiMap[two]!;
            index += 2;
            continue;
        }

        // handle small tsu for double consonants
        if (two[0] && two[0] === two[1] && /[bcdfghjklmpqrstvwxyz]/.test(two[0]!)) {
            result += 'っ';
            index += 1;
            continue;
        }

        if (romajiMap[one]) {
            result += romajiMap[one]!;
        } else {
            // Keep unknown characters so the user sees what was not converted.
            result += one;
        }
        index += 1;
    }

    return result;
}
